import { TCargoEOS } from '@types';

export type TCargoStopGroups = {
	/** Cargo indexes sharing each pick-up stop, in the order the stops first appear. */
	pickups: number[][];
	/** The same per drop-off. A shipment has exactly one, so this is normally a single group. */
	dropoffs: number[][];
};

/* The stop ordinal the backend stored against the cargo. It is what the server's own repricing keys
   on when it collapses cargos into stops, so grouping by it here means the two agree by
   construction. Zero is a valid position, hence the explicit null check rather than truthiness. */
const getStopKey = (cargo: TCargoEOS, index: number, actionType: 'pickup' | 'dropoff') => {
	const position = actionType === 'pickup' ? cargo?.pick_position : cargo?.drop_position;
	if (position !== null && position !== undefined) {
		return `position:${position}`;
	}
	/* EOS shipments booked before positions were stored have no ordinal, so fall back to the
	   ship_address row the cargo points at. */
	const stop = cargo?.[actionType];
	if (stop?.id) return `address-id:${stop.id}`;
	// Nothing identifies the stop yet, so nothing is known to be shared — keep the cargo on its own.
	return `cargo:${index}`;
};

const groupByActionType = (cargos: TCargoEOS[], actionType: 'pickup' | 'dropoff'): number[][] => {
	const groupsByKey = new Map<string, number[]>();
	cargos.forEach((cargo, index) => {
		const key = getStopKey(cargo, index, actionType);
		const group = groupsByKey.get(key);
		if (group) {
			group.push(index);
			return;
		}
		groupsByKey.set(key, [index]);
	});
	return [...groupsByKey.values()];
};

/**
 * Groups cargos the backend put on the same stop, as indexes into the cargos array and in
 * first-appearance order. `{ pickups: [[0, 1], [2]] }` means cargos 0 and 1 share a pick-up.
 *
 * Mirrors the shipment API's cargo_stops join, which pairs every cargo with a pick-up and a
 * drop-off. Callers want `pickups`; a shipment only ever has one drop-off, so `dropoffs` is normally
 * a single group holding every cargo.
 *
 * For a saved shipment. Keyed on stop identity rather than address text, so it only ever collapses
 * what the payload actually merged — showing two pick-ups as one would otherwise misrepresent what
 * the carrier was sent.
 */
export const groupCargosByStop = (cargos: TCargoEOS[]): TCargoStopGroups => ({
	pickups: groupByActionType(cargos, 'pickup'),
	dropoffs: groupByActionType(cargos, 'dropoff'),
});
